import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, InputLabel, Stack } from "@mui/material";
import dayjs from "dayjs";

import {
  getSitesEmissions,
  getSitesEmissionsBySite,
  SitesEmissionsDTO,
} from "api";
import {
  useGetAllowedValues,
  useGetConfigParameter,
  useNotify,
  useSites,
  useUtilityBillsYears,
} from "hooks";
import { ILoggedUserData } from "types";
import { useNewBreadcrumb } from "recoils";
import {
  getDateFormatmonthset,
  getFiscalYear,
  getLoggedUserData,
  calculateCurrentFiscalYear,
} from "utils";
import {
  COLORS,
  DashboardTabs,
  EsgDatePickerField,
  MarginWrapper,
  MultiSelectControlledOption,
  Switch,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import { AddDateModal } from "./components";
import { SITES_EMISSIONS_PAGE, mainTabs } from "./consts";
import { getAllTabs, getSitesFiltered, toggleSelectionValues } from "./helpers";

import { SelectField } from "../documents/missing-documents/components";

export const SitesEmissionsPage = () => {
  useNewBreadcrumb(SITES_EMISSIONS_PAGE.TITLE);

  const notify = useNotify();
  const loggedUserData: ILoggedUserData = getLoggedUserData();

  const { data: configParameter } = useGetConfigParameter();
  const { data: availableYears } = useUtilityBillsYears();
  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();

  const { mutateAsync: getSitesEmissionsRequest } =
    useMutation(getSitesEmissions);
  const { mutateAsync: getSitesEmissionsBySiteRequest } = useMutation(
    getSitesEmissionsBySite,
  );

  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingSite, setIsLoadingSite] = useState(false);

  const loadingSitesEmissions = isLoadingSummary || isLoadingSite;

  const isAdminUser =
    loggedUserData?.roles?.some(
      (rol: string) => rol === "MODULE_ADMIN" || rol === "SUPER_ADMIN",
    ) || false;

  const companyMonths = useMemo(
    () =>
      (configParameter &&
        configParameter
          .filter((param: any) => param.name.includes("company_month"))
          .map((param: any) => param.configValue)) || ["Jan", "Dec"],
    [configParameter],
  );

  const allFiscalYears: string[] = useMemo(
    () =>
      (availableYears &&
        availableYears.map((fy: number) => `${fy} Fiscal Year`)) || [
        `${dayjs().get("year")} Fiscal Year`,
      ],
    [availableYears],
  );

  const utilityTypes = useMemo(
    () =>
      (allowedValues &&
        allowedValues.utilityTypes
          .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
          .map((ut: any) => ({
            name: ut.name,
            label: ut.name,
            value: ut.id,
          }))) ||
      [],
    [allowedValues],
  );

  const sites = useMemo(
    () =>
      (sitesData &&
        sitesData.map((site: any) => ({
          name: site.name,
          label: site.name,
          value: site.id,
        }))) ||
      [],
    [sitesData],
  );

  const businessUnits = useMemo(
    () =>
      (allowedValues &&
        allowedValues.businessUnits
          .sort()
          .map((bu: string) => ({ name: bu, label: bu, value: bu }))) ||
      [],
    [allowedValues],
  );

  const countryRegions = useMemo(
    () =>
      (allowedValues &&
        allowedValues.countryRegions
          .sort()
          .map((cr: string) => ({ name: cr, label: cr, value: cr }))) ||
      [],
    [allowedValues],
  );

  const defaultFY = useMemo(
    () => calculateCurrentFiscalYear(companyMonths),
    [companyMonths],
  );

  const [viewType, setViewType] = useState<string>("monthly");
  const [isDisaggregated, setIsDisaggregated] = useState<boolean>(true);
  const [currentEmissionsData, setCurrentEmissionsData] =
    useState<SitesEmissionsDTO | null>(null);
  const [currentSiteAggregations, setCurrentSiteAggregations] =
    useState<NonNullable<SitesEmissionsDTO["siteAggregations"]> | null>(null);
  const [currentFY, setCurrentFY] = useState<string>(defaultFY);
  const [areSitesFiltered, setAreSitesFiltered] = useState<boolean>(false);
  const [dateError, setDateError] = useState<boolean>(false);

  const [currentUtilityTypeIds, setCurrentUtilityTypeIds] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentSiteIds, setCurrentSiteIds] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentCountryRegions, setCurrentCountryRegions] = useState<
    MultiSelectControlledOption[]
  >([]);
  const kpiRefetchFn = useRef<((query: any) => void) | null>(null);
  const lastRequestParams = useRef<string | null>(null);
  const lastSiteRequestParams = useRef<string | null>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().year()]))}-01-01`,
  );
  const maxDate = dayjs().endOf("day");

  const fiscalYearData = getFiscalYear(
    companyMonths,
    currentFY.substring(0, 4),
  );

  const defaultObject = useRef<any>({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    viewType,
    siteIds: [],
    utilityTypeIds: null,
    businessUnits: null,
    countryRegions: null,
    isDisaggregated: String(isDisaggregated),
  });

  const handleChangeIsDisaggregated = (isDisaggregatedValue: boolean) => {
    setIsDisaggregated(isDisaggregatedValue);
    defaultObject.current = {
      ...defaultObject.current,
      isDisaggregated: String(isDisaggregatedValue),
    };
  };

  const handleKpiRefetchReady = (refetchFn: (query: any) => void) => {
    kpiRefetchFn.current = refetchFn;
  };

  const allTabs = getAllTabs(
    isAdminUser,
    currentEmissionsData,
    currentSiteAggregations,
    isLoadingSummary,
    isLoadingSite,
    isDisaggregated,
    handleChangeIsDisaggregated,
    defaultObject.current,
    configParameter,
    handleKpiRefetchReady,
  );

  const viewAsDate = (date: string) => {
    defaultObject.current = {
      ...defaultObject.current,
      endDate: dayjs(date).format("YYYY-MM-DD"),
    };
    handleUpdateSitesEmissions();
  };

  const handleSwitchChange = () => {
    const newViewType = viewType === "yearly" ? "monthly" : "yearly";

    let startDate: string;
    let endDate: string;

    if (newViewType === "yearly") {
      startDate = fiscalYearData.fromDate;
      endDate = fiscalYearData.endDate;
    } else {
      startDate = dayjs().startOf("month").format("YYYY-MM-DD");
      endDate = dayjs().format("YYYY-MM-DD");
    }

    setViewType(newViewType);
    defaultObject.current = {
      ...defaultObject.current,
      startDate,
      endDate,
      viewType: newViewType,
    };

    handleUpdateSitesEmissions();
  };

  const handleClearFilters = () => {
    setCurrentFY(defaultFY);
    setCurrentUtilityTypeIds([]);
    setCurrentSiteIds([]);
    setCurrentBusinessUnits([]);
    setCurrentCountryRegions([]);
    setIsDisaggregated(true);
    setAreSitesFiltered(false);
    defaultObject.current = {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
      viewType,
      siteIds: [],
      utilityTypeIds: null,
      businessUnits: null,
      countryRegions: null,
      isDisaggregated: String(isDisaggregated),
    };
  };

  const handleDateChange = (date: string, type: string) => {
    if (dayjs(date).isValid()) {
      setDateError(false);

      let formattedDate = dayjs(date).format("YYYY-MM-DD");

      if (type === "endDate") {
        const selectedDate = dayjs(date);
        const currentDate = dayjs();

        if (
          selectedDate.isSame(currentDate, "month") &&
          selectedDate.isSame(currentDate, "year")
        ) {
          formattedDate = currentDate.format("YYYY-MM-DD");
        } else {
          formattedDate = selectedDate.endOf("month").format("YYYY-MM-DD");
        }
      }

      defaultObject.current = {
        ...defaultObject.current,
        [type]: formattedDate,
      };
    } else {
      setDateError(true);
    }
  };

  const normalizeParams = (params: any) => ({
    ...params,
    siteIds: Array.isArray(params.siteIds)
      ? [...params.siteIds].sort((a, b) => a - b)
      : params.siteIds,
    utilityTypeIds: Array.isArray(params.utilityTypeIds)
      ? [...params.utilityTypeIds].sort((a, b) => a - b)
      : params.utilityTypeIds,
    businessUnits: Array.isArray(params.businessUnits)
      ? [...params.businessUnits].sort()
      : params.businessUnits,
    countryRegions: Array.isArray(params.countryRegions)
      ? [...params.countryRegions].sort()
      : params.countryRegions,
  });

  const handleFetchSummaryData = async () => {
    const sitesEmissionsRequestBody = { ...defaultObject.current };
    const normalizedParams = normalizeParams(sitesEmissionsRequestBody);
    const requestParamsString = JSON.stringify(normalizedParams);

    // Skip if params haven't changed and we're not already loading
    if (
      lastRequestParams.current === requestParamsString &&
      !isLoadingSummary
    ) {
      return;
    }
    lastRequestParams.current = requestParamsString;

    setIsLoadingSummary(true);
    getSitesEmissionsRequest(sitesEmissionsRequestBody)
      .then((summaryData) => {
        // Merge with existing data to preserve siteAggregations if they exist
        setCurrentEmissionsData((prev) =>
          summaryData
            ? {
                ...summaryData,
                siteAggregations:
                  prev?.siteAggregations ??
                  summaryData.siteAggregations ??
                  null,
              }
            : prev,
        );
      })
      .catch((error) => {
        notify.error(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to load Summary Data.",
        );
      })
      .finally(() => {
        setIsLoadingSummary(false);
      });
  };

  const handleFetchSiteData = async () => {
    const sitesEmissionsRequestBody = { ...defaultObject.current };
    const normalizedParams = normalizeParams(sitesEmissionsRequestBody);
    const requestParamsString = JSON.stringify(normalizedParams);

    // Only call if parameters changed after the first call
    if (
      lastSiteRequestParams.current === requestParamsString &&
      !isLoadingSite
    ) {
      return;
    }
    lastSiteRequestParams.current = requestParamsString;

    setIsLoadingSite(true);
    getSitesEmissionsBySiteRequest(sitesEmissionsRequestBody)
      .then((siteData) => {
        if (siteData?.siteAggregations) {
          setCurrentSiteAggregations(siteData.siteAggregations);
          // Also update currentEmissionsData with siteAggregations
          setCurrentEmissionsData((prev) => {
            if (prev) {
              return {
                ...prev,
                siteAggregations: siteData.siteAggregations,
              };
            }
            return null;
          });
        }
      })
      .catch((error) => {
        notify.error(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to load Site Aggregations Data.",
        );
      })
      .finally(() => {
        setIsLoadingSite(false);
      });
  };

  const handleTabChange = (newTabIndex: number) => {
    setCurrentTabIndex(newTabIndex);

    // Tab 0 is "Summary" (ConsumptionSummary) - call getSitesEmissions
    if (newTabIndex === 0) {
      handleFetchSummaryData();
    }
    // Tabs 1, 2, 3 are "Location Based", "Market Based", "Emissions Intensity" - call getSitesEmissionsBySite
    else if (newTabIndex === 1 || newTabIndex === 2 || newTabIndex === 3) {
      handleFetchSiteData();
    }
  };

  const handleUpdateSitesEmissions = async () => {
    if (kpiRefetchFn.current) {
      kpiRefetchFn.current(defaultObject.current);
    }

    // Call the appropriate API based on current tab
    if (currentTabIndex === 0) {
      handleFetchSummaryData();
    } else if (
      currentTabIndex === 1 ||
      currentTabIndex === 2 ||
      currentTabIndex === 3
    ) {
      handleFetchSiteData();
    }
  };

  useEffect(() => {
    // When isDisaggregated changes, update the appropriate API based on current tab
    if (currentTabIndex === 0) {
      handleFetchSummaryData();
    } else if (
      currentTabIndex === 1 ||
      currentTabIndex === 2 ||
      currentTabIndex === 3
    ) {
      handleFetchSiteData();
    }
  }, [isDisaggregated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fy = calculateCurrentFiscalYear(companyMonths);
    if (currentFY !== fy) {
      setCurrentFY(fy);
    }
  }, [companyMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load: fetch Summary data when component mounts (tab 0 is default)
  useEffect(() => {
    handleFetchSummaryData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MarginWrapper>
      <Box
        sx={{
          "@media (max-width: 1100px)": {
            left: "345px",
          },
          "@media (min-width: 1100px)": {
            right: "568px",
          },
          width: "160px",
          "& span:first-of-type > button": {
            width: "160px",
          },
          position: "absolute",
          top: "-5px",
          right: loadingSitesEmissions ? "500px" : "420px",
          cursor: "pointer",
        }}
      >
        <AddDateModal
          viewAsDate={viewAsDate}
          disabled={viewType === "monthly"}
          year={currentFY}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ mt: 3, width: "100px" }}>
          <Switch
            isChecked={viewType === "yearly"}
            label={viewType === "yearly" ? "Year" : "Range"}
            onChange={handleSwitchChange}
          />
        </Box>
        {viewType === "yearly" && (
          <Box
            sx={{
              width: "200px",
              "& > div:first-of-type": {
                height: "35px",
              },
            }}
          >
            <InputLabel
              sx={{
                fontSize: "12px",
                color: "black",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Fiscal Year
            </InputLabel>
            <SelectField
              options={allFiscalYears}
              initialValue={currentFY}
              onChangeFn={(event: string) => {
                setCurrentFY(event);
                defaultObject.current = {
                  ...defaultObject.current,
                  startDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .fromDate,
                  endDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .endDate,
                };
              }}
              placeholder="Select Fiscal Year"
            />
          </Box>
        )}
        {viewType === "monthly" && (
          <Stack direction="row" spacing={2}>
            <EsgDatePickerField
              width="200px"
              label="From Month"
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableFuture
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "startDate")}
              onChange={(e: any) => handleDateChange(e, "startDate")}
              setDate={(e: any) => handleDateChange(e, "startDate")}
              value={dayjs(defaultObject.current.startDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
              clearDate={false}
            />
            <EsgDatePickerField
              width="200px"
              label="To Month"
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableFuture
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "endDate")}
              onChange={(e: any) => handleDateChange(e, "endDate")}
              setDate={(e: any) => handleDateChange(e, "endDate")}
              value={dayjs(defaultObject.current.endDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
              clearDate={false}
            />
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Utility Type"
            selectedValues={currentUtilityTypeIds}
            setSelectedValues={setCurrentUtilityTypeIds}
            options={utilityTypes}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                utilityTypeIds: toggleSelectionValues(
                  currentUtilityTypeIds,
                  value,
                  unchecked,
                ),
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Sites"
            selectedValues={currentSiteIds}
            setSelectedValues={setCurrentSiteIds}
            options={
              !areSitesFiltered
                ? sites
                : getSitesFiltered(sitesData || [], currentCountryRegions)
            }
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                siteIds:
                  toggleSelectionValues(currentSiteIds, value, unchecked) ?? [],
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Business Unit"
            selectedValues={currentBusinessUnits}
            setSelectedValues={setCurrentBusinessUnits}
            options={businessUnits}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                businessUnits: toggleSelectionValues(
                  currentBusinessUnits,
                  value,
                  unchecked,
                ),
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Region"
            selectedValues={currentCountryRegions}
            setSelectedValues={setCurrentCountryRegions}
            options={countryRegions}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                countryRegions: toggleSelectionValues(
                  currentCountryRegions,
                  value,
                  unchecked,
                ),
              };
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pt: "20px",
            gap: "16px",
          }}
        >
          <Button
            sx={{
              width: "100px",
              border: "1px solid #054254",
              borderRadius: "8px",
            }}
            onClick={handleUpdateSitesEmissions}
            disabled={dateError}
          >
            Search
          </Button>
          <Button
            sx={{
              width: "120px",
              border: "1px solid #054254",
              borderRadius: "8px",
            }}
            onClick={handleClearFilters}
          >
            Clear Dates
          </Button>
        </Box>
      </Box>
      <DashboardTabs
        intensity
        tabs={allTabs}
        selectedBtnOption={mainTabs.INITIAL_TAB}
        dashboard
        onTabChange={handleTabChange}
      />
    </MarginWrapper>
  );
};

export default SitesEmissionsPage;
